import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SessionResult } from '@spectrace/shared';
import { ApiService } from './api.service';
import { SseService } from './sse.service';
import { TeamContextService } from './team-context.service';
import { AuthService } from './auth.service';
import { SessionService } from './session.service';

export type SessionActivityLevel = 'info' | 'success' | 'warning' | 'error';

export interface SessionActivityEvent {
  id: string;
  sessionId: string;
  userId: string;
  teamId: string;
  category: 'session' | 'participant' | 'telemetry' | 'lap' | 'stint';
  action: string;
  level: SessionActivityLevel;
  message: string;
  createdAt: string;
  playerName?: string;
  lapNumber?: number;
  details?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class LeaderboardService {
  private entriesSubject = new BehaviorSubject<SessionResult[]>([]);
  private telemetryStatusSubject = new BehaviorSubject<boolean>(false);
  private sessionActivitiesSubject = new BehaviorSubject<SessionActivityEvent[]>(
    [],
  );

  public entries$ = this.entriesSubject.asObservable();
  public activeSession$ = this.sessionService.activeSession$;
  public telemetryStatus$ = this.telemetryStatusSubject.asObservable();
  public sessionActivities$ = this.sessionActivitiesSubject.asObservable();

  private initialized = false;

  constructor(
    private apiService: ApiService,
    private sseService: SseService,
    private teamContextService: TeamContextService,
    private authService: AuthService,
    private sessionService: SessionService,
  ) {
    this.authService.currentUser$.subscribe((user) => {
      if (!user) {
        this.initialized = false;
        this.sseService.disconnect();
        this.entriesSubject.next([]);
        this.sessionService.clearActiveSession();
        this.telemetryStatusSubject.next(false);
        this.sessionActivitiesSubject.next([]);
      }
    });

    // Keep leaderboard data aligned with selected team context.
    this.teamContextService.currentTeam$.subscribe(() => {
      this.applyTeamFilterToCurrentState();
      if (this.initialized && this.authService.isAuthenticated()) {
        this.loadLeaderboard();
      }
    });
  }

  public initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.initializeSSE();
    this.loadLeaderboard();
  }

  private initializeSSE(): void {
    // Pass auth token and current team scope to the SSE endpoint.
    // The backend now requires authentication on /api/events, and the native
    // EventSource API cannot send custom headers, so token is a query param.
    const token = this.authService.getToken() || '';
    const teamId = this.teamContextService.getCurrentTeamId();

    this.sseService.connect(token, teamId).subscribe({
      next: (event) => {
        switch (event.type) {
          case 'telemetryStatus':
            this.telemetryStatusSubject.next(event['connected']);
            break;
          case 'leaderboard':
            const entries = this.filterEntriesForCurrentTeam(
              event['entries'] || [],
            );
            const activeSession = this.filterActiveSessionForCurrentTeam(
              event['activeSession'] || null,
            );
            console.log(
              `📥 Frontend: Received leaderboard - ${
                entries.length
              } entries, active session: ${activeSession?.id || 'none'}`,
            );
            if (entries.length > 0) {
              console.log(
                `   First entry: ${entries[0].id}, laps: ${
                  entries[0].allLaps?.length || 0
                }`,
              );
            }
            // Emit activeSession BEFORE entries to avoid race condition
            this.sessionService.setActiveSession(activeSession);
            this.entriesSubject.next(entries);
            break;
          case 'sessionStarted':
            if (this.shouldIncludeSession(event['session'] || null)) {
              this.sessionService.setActiveSession(event['session']);
            }
            break;
          case 'sessionEnded':
            if (this.shouldIncludeSession(event['session'] || null)) {
              this.sessionService.clearActiveSession();
            }
            break;
          case 'stintStarted':
            // Update current session with new stint info
            const currentSession =
              this.sessionService.getActiveSessionSnapshot();
            if (currentSession) {
              const newStint = event['stint'];
              if (!newStint || newStint.sessionId !== currentSession.id) {
                break;
              }
              const currentActiveStints = currentSession.activeStints || [];
              // Add to activeStints array if not already present
              if (
                !currentActiveStints.some((s) => s.id === newStint.id)
              ) {
                this.sessionService.setActiveSession({
                  ...currentSession,
                  activeStints: [...currentActiveStints, newStint],
                });
              }
            }
            break;
          case 'stintEnded':
            // Update current session to remove stint
            const session = this.sessionService.getActiveSessionSnapshot();
            if (session) {
              const endedStint = event['stint'];
              if (!endedStint || endedStint.sessionId !== session.id) {
                break;
              }
              const activeStints = session.activeStints || [];
              // Remove from activeStints array
              this.sessionService.setActiveSession({
                ...session,
                activeStints: activeStints.filter((s) => s.id !== endedStint.id),
              });
            }
            break;
          case 'leaderboardCleared':
            this.entriesSubject.next([]);
            break;
          case 'sessionActivityHistory':
            this.appendSessionActivities(event['activities'] || []);
            break;
          case 'sessionActivity':
            this.appendSessionActivities([event['activity']]);
            break;
        }
      },
      error: (error) => {
        console.error('SSE error:', error);
      },
    });
  }

  private loadLeaderboard(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    const teamId = this.teamContextService.getCurrentTeamId() || undefined;
    this.apiService.getBestLaps(teamId).subscribe({
      next: (response) => {
        // Emit activeSession BEFORE entries to avoid race condition in components
        // that check activeSession when processing entries
        this.sessionService.setActiveSession(
          this.filterActiveSessionForCurrentTeam(response.activeSession),
        );
        this.entriesSubject.next(
          this.filterEntriesForCurrentTeam(response.entries || []),
        );
      },
      error: (error) => {
        console.error('Failed to load best laps:', error);
      },
    });
  }

  private applyTeamFilterToCurrentState(): void {
    this.sessionService.setActiveSession(
      this.filterActiveSessionForCurrentTeam(
        this.sessionService.getActiveSessionSnapshot(),
      ),
    );
    this.entriesSubject.next(
      this.filterEntriesForCurrentTeam(this.entriesSubject.value),
    );
  }

  private filterEntriesForCurrentTeam(
    entries: SessionResult[],
  ): SessionResult[] {
    const teamId = this.teamContextService.getCurrentTeamId();
    if (!teamId) {
      return entries;
    }
    return entries.filter((entry) => entry.teamId === teamId);
  }

  private filterActiveSessionForCurrentTeam(
    session: SessionResult | null,
  ): SessionResult | null {
    const teamId = this.teamContextService.getCurrentTeamId();
    if (!teamId) {
      return session;
    }
    return session && session.teamId === teamId ? session : null;
  }

  private shouldIncludeSession(session: SessionResult | null): boolean {
    const teamId = this.teamContextService.getCurrentTeamId();
    if (!teamId) {
      return true;
    }
    return !!session && session.teamId === teamId;
  }

  clearLeaderboard(): Observable<any> {
    return this.apiService.clearLeaderboard();
  }

  refreshLeaderboard(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    this.loadLeaderboard();
  }

  deleteEntry(id: string): Observable<any> {
    return this.apiService.deleteEntry(id);
  }

  deleteLap(entryId: string, lapNumber: number): Observable<any> {
    return this.apiService.deleteLap(entryId, lapNumber);
  }

  hydrateSessionActivities(activities: unknown[]): void {
    this.appendSessionActivities(activities);
  }

  private appendSessionActivities(activities: unknown[]): void {
    if (!Array.isArray(activities) || activities.length === 0) {
      return;
    }

    const parsed = activities
      .map((activity) => this.coerceSessionActivity(activity))
      .filter((activity): activity is SessionActivityEvent => !!activity);

    if (parsed.length === 0) {
      return;
    }

    const existing = this.sessionActivitiesSubject.value;
    const merged = [...existing, ...parsed];
    const dedupedById = new Map<string, SessionActivityEvent>();
    for (const activity of merged) {
      dedupedById.set(activity.id, activity);
    }

    const sorted = Array.from(dedupedById.values()).sort((a, b) => {
      return (
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    });
    const maxEvents = 500;
    const trimmed =
      sorted.length > maxEvents
        ? sorted.slice(sorted.length - maxEvents)
        : sorted;
    this.sessionActivitiesSubject.next(trimmed);
  }

  private coerceSessionActivity(activity: unknown): SessionActivityEvent | null {
    if (!activity || typeof activity !== 'object') {
      return null;
    }

    const source = activity as Partial<SessionActivityEvent>;
    if (!source.id || !source.sessionId || !source.message || !source.createdAt) {
      return null;
    }

    return {
      id: String(source.id),
      sessionId: String(source.sessionId),
      userId: String(source.userId || ''),
      teamId: String(source.teamId || ''),
      category:
        source.category === 'session' ||
        source.category === 'participant' ||
        source.category === 'telemetry' ||
        source.category === 'lap' ||
        source.category === 'stint'
          ? source.category
          : 'session',
      action: String(source.action || 'unknown'),
      level:
        source.level === 'success' ||
        source.level === 'warning' ||
        source.level === 'error' ||
        source.level === 'info'
          ? source.level
          : 'info',
      message: String(source.message),
      createdAt: String(source.createdAt),
      playerName:
        typeof source.playerName === 'string' ? source.playerName : undefined,
      lapNumber:
        typeof source.lapNumber === 'number' ? source.lapNumber : undefined,
      details:
        source.details && typeof source.details === 'object'
          ? source.details
          : undefined,
    };
  }
}
